import { AxiosResponse } from 'axios';
import { csrfToken, instance } from '.';
import { productStatus } from '../../constants/Enum';
import {
  AdminProductStatusType,
  DocumentDataType,
  ImageDataType,
  MyProductGetListType,
  ProductGetListType,
  ProductInfosType
} from '../../validation/products/productType';
import dayjs, { Dayjs } from 'dayjs';

// 建材一覧取得
export const getProductList = async (
  limit: number,
  offset: number,
  order: number,
  method: number,
  categoryId?: number | null,
  materialId?: number | null,
  keyword?: string | null,
  calculationBasisType?: (number | null)[],
  carbonOffsetFlg?: number | null,
  certificationFlg?: number | null,
  recommendFlg?: number | null,
  favoriteMaterialsFlg?: number | null,
  evaluationFlg?: number | null
): Promise<AxiosResponse<ProductGetListType>> => {
  try {
    // パラメータを表示するための配列を作成します。
    const params = [
      ['limit', limit],
      ['offset', offset],
      ['order', order],
      ['method', method],
      [
        'category_id',
        typeof categoryId === 'number' && isNaN(categoryId) ? null : categoryId
      ],
      [
        'material_id',
        typeof materialId === 'number' && isNaN(materialId) ? null : materialId
      ],
      ['free_word', keyword],
      [
        'calculation_basis_type',
        calculationBasisType ? calculationBasisType : null
      ],
      ['carbon_offset_flg', carbonOffsetFlg],
      ['certification_flg', certificationFlg],
      ['recommend', recommendFlg],
      ['evaluation', evaluationFlg]
    ];
    // すべてのパラメータをループし、それらが空または未定義ではないことを確認します。
    // それらが空または未定義の場合、それらを省略します。
    let query = '';
    params.forEach(([name, value], index) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach((val, arrIndex) => {
            if (val !== null) {
              if (index !== 0 || arrIndex !== 0) query += '&';
              query += `${name}[]=${encodeURIComponent(val.toString())}`;
            }
          });
          return;
        }
        if (index !== 0) query += '&';
        query += `${name}=${encodeURIComponent(value.toString())}`;
      }
    });

    let response;
    if (favoriteMaterialsFlg) {
      response = await instance.get(`/favorite?${query}`);
    } else {
      response = await instance.get(`/products?${query}`);
    }
    return response;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (err.response && err.response.data && err.response.data.message) {
      throw new Error(err.response.data.message);
    } else {
      throw err;
    }
  }
};

export const getOperatorProductList = async (
  limit: number,
  offset: number,
  order: number,
  method: number,
  categoryId?: number | null,
  materialId?: number | null,
  keyword?: string | null,
  calculationBasisType?: (number | null)[],
  carbonOffsetFlg?: number | null,
  certificationFlg?: number | null,
  recommendFlg?: number | null,
  evaluationFlg?: number | null,
  status?: number[] | null,
  companyName?: string | null
): Promise<AxiosResponse<ProductGetListType>> => {
  try {
    status = status?.filter((item) => item !== 0);
    // パラメータを表示するための配列を作成します。
    const params = [
      ['limit', limit],
      ['offset', offset],
      ['order', order],
      ['method', method],
      [
        'category_id',
        typeof categoryId === 'number' && isNaN(categoryId) ? null : categoryId
      ],
      [
        'material_id',
        typeof materialId === 'number' && isNaN(materialId) ? null : materialId
      ],
      ['free_word', keyword],
      [
        'calculation_basis_type',
        calculationBasisType ? calculationBasisType : null
      ],
      ['carbon_offset_flg', carbonOffsetFlg],
      ['certification_flg', certificationFlg],
      ['recommend', recommendFlg],
      ['evaluation', evaluationFlg],
      ['status', status],
      ['company_name', companyName]
    ];

    // すべてのパラメータをループし、それらが空または未定義ではないことを確認します。
    // それらが空または未定義の場合、それらを省略します。
    let query = '';
    params.forEach(([name, value], index) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach((val, arrIndex) => {
            if (val !== null) {
              if (index !== 0 || arrIndex !== 0) query += '&';
              query += `${name}[]=${encodeURIComponent(val.toString())}`;
            }
          });
          return;
        }
        if (index !== 0) query += '&';
        query += `${name}=${encodeURIComponent(value.toString())}`;
      }
    });

    return await instance.get(`/operator/products?${query}`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (err.response && err.response.data && err.response.data.message) {
      throw new Error(err.response.data.message);
    } else {
      throw err;
    }
  }
};

// 建材登録
export const registerProduct = async (
  kind: number,
  watch: (fieldName: string) => ProductInfosType
): Promise<AxiosResponse> => {
  const settingAt = watch('settingAt');
  const formattedSettingAt = (settingAt as unknown as Dayjs)?.format(
    'YYYY-MM-DD HH:mm:ss'
  );

  const requestBody = {
    kind: kind,
    alias: watch('alias'),
    name: watch('name'),
    specification: watch('specification') ?? null,
    summary: watch('summary'),
    condition: watch('condition') ?? null,
    images: watch('images') ?? null,
    document: watch('document') ?? null,
    document_description_url: watch('documentDescriptionUrl') ?? null,
    category_id: Number(watch('categoryId')) !== 0 ? watch('categoryId') : null,
    material_id: Number(watch('materialId')) !== 0 ? watch('materialId') : null,
    emission_intensity: watch('emissionIntensity')
      ? Number(watch('emissionIntensity'))
      : null,
    reduce_rate: watch('reduceRate') ? Number(watch('reduceRate')) : null,
    calculation_supplement: watch('calculationSupplement') ?? null,
    calculation_basis_type: Number(watch('calculationBasisType')) ?? null,
    calculation_basis_name: watch('calculationBasisName') ?? null,
    calculation_basis_file: watch('calculationBasisFile') ?? null,
    calculation_basis_url: watch('calculationBasisFileUrl') ?? null,
    carbon_offset_flg: watch('carbonOffsetFlg') ?? null,
    other_certification: watch('otherCertification') ?? null,
    reference_unit_price: watch('referenceUnitPrice')
      ? Number(watch('referenceUnitPrice'))
      : null,
    setting_at: formattedSettingAt ?? null,
    price_remarks: watch('priceRemarks') ?? null,
    related_url: watch('relatedUrl') ?? null,
    remark: watch('remark') ?? null,
    assignee: watch('assignee') ?? null
  };

  const response = await instance.post('/products', requestBody, {
    headers: {
      'X-XSRF-TOKEN': csrfToken
    }
  });

  return response;
};

// 建材更新時の日付のフォーマット
const dateFormatter = (watch: ProductInfosType) => {
  const settingAt = watch;
  if (settingAt !== null) {
    return dayjs(settingAt as unknown as string).format('YYYY-MM-DD HH:mm:ss');
  }
};

// 建材更新時のimageの配列を作成
const imageMapToArray = (watch: ProductInfosType) => {
  const data = watch;

  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((item: ImageDataType) => {
    if (item.kind === 'new') {
      return {
        kind: 'new',
        name: item.name,
        binary: item.binary,
        sort: item.sort
      };
    } else if (item.kind === 'exist') {
      return {
        kind: 'exist',
        id: item.id,
        sort: item.sort
      };
    } else if (item.kind === 'del') {
      return {
        kind: 'del',
        id: item.id,
        sort: 99
      };
    }
  });
};

// 編集時のdocumentの配列を作成
const documentData = (watch: DocumentDataType) => {
  if (watch.kind === 'new') {
    return watch;
  } else if (watch.kind === 'exist') {
    return {
      kind: 'exist'
    };
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseToNumberOrNull = (value: any): number | null => {
  return value === '' || value === null || value === undefined
    ? null
    : Number(value);
};

// 建材取得
export const getProduct = async (
  productId: number,
  method: number,
  productInfoId?: number | null
): Promise<AxiosResponse> => {
  const url = productInfoId
    ? `/products/${productId}?method=${method}&product_info_id=${productInfoId}`
    : `/products/${productId}?method=${method}`;
  const response = await instance.get(url, {
    headers: {
      'X-XSRF-TOKEN': csrfToken
    }
  });
  return response;
};

// 登録した建材取得
export const getMyProductList = async (
  limit: number,
  offset: number,
  order: number,
  method: number
): Promise<MyProductGetListType> => {
  const response = await instance.get(
    `/products/my_product?limit=${limit}&offset=${offset}&order=${order}&method=${method}`,
    {
      headers: {
        'X-XSRF-TOKEN': csrfToken
      }
    }
  );
  return response.data;
};

// 登録した建材取得（指定したメンバーのみ）
export const getMemberProductList = async (
  limit: number,
  offset: number,
  order: number,
  method: number,
  memberId?: number
): Promise<MyProductGetListType> => {
  const response = await instance.get(
    `/products/my_product?limit=${limit}&offset=${offset}&order=${order}&method=${method}&member_id=${memberId}`,
    {
      headers: {
        'X-XSRF-TOKEN': csrfToken
      }
    }
  );
  return response.data;
};

// 建材編集
export const updateProduct = async (
  productId: number,
  watch: (fieldName: string) => ProductInfosType
): Promise<AxiosResponse> => {
  const requestBody = {
    alias: watch('alias'),
    name: watch('name'),
    specification: watch('specification') ?? null,
    summary: watch('summary'),
    condition: watch('condition') ?? null,
    images: imageMapToArray(watch('images')),
    document: watch('document') ? documentData(watch('document')) : null,
    document_description_url: watch('documentDescriptionUrl') ?? null,
    category_id: watch('categoryId'),
    material_id: watch('materialId'),
    emission_intensity: watch('emissionIntensity')
      ? Number(watch('emissionIntensity'))
      : null,
    reduce_rate: watch('reduceRate') ? Number(watch('reduceRate')) : null,
    calculation_supplement: watch('calculationSupplement') ?? null,
    calculation_basis_type: Number(watch('calculationBasisType')),
    calculation_basis_name: watch('calculationBasisName') ?? null,
    calculation_basis_file: watch('calculationBasisFile')
      ? documentData(watch('calculationBasisFile'))
      : null,
    calculation_basis_url: watch('calculationBasisFileUrl') ?? null,
    carbon_offset_flg: watch('carbonOffsetFlg') ?? null,
    other_certification: watch('otherCertification') ?? null,
    reference_unit_price: watch('referenceUnitPrice')
      ? Number(watch('referenceUnitPrice'))
      : null,
    setting_at: dateFormatter(watch('settingAt')) ?? null,
    price_remarks: watch('priceRemarks') ?? null,
    related_url: watch('relatedUrl') ?? null,
    remark: watch('remark') ?? null,
    assignee: watch('assignee') ?? null
  };

  const response = await instance.post(`/products/${productId}`, requestBody, {
    headers: {
      'X-XSRF-TOKEN': csrfToken
    }
  });

  return response;
};

// 建材下書き更新API
export const saveDraftProduct = async (
  productId: number,
  productInfoId: number,
  kind: number,
  watch: (fieldName: string) => ProductInfosType
): Promise<AxiosResponse> => {
  const requestBody = {
    kind: kind,
    alias: watch('alias'),
    name: watch('name'),
    specification: watch('specification') ?? null,
    summary: watch('summary'),
    condition: watch('condition') ?? null,
    images: imageMapToArray(watch('images')),
    document: watch('document') ?? null,
    document_description_url: watch('documentDescriptionUrl') ?? null,
    category_id: Number(watch('categoryId')) !== 0 ? watch('categoryId') : null,
    material_id: Number(watch('materialId')) !== 0 ? watch('materialId') : null,
    emission_intensity: watch('emissionIntensity')
      ? Number(watch('emissionIntensity'))
      : null,
    reduce_rate: watch('reduceRate') ? Number(watch('reduceRate')) : null,
    calculation_supplement: watch('calculationSupplement') ?? null,
    calculation_basis_type: Number(watch('calculationBasisType')) ?? null,
    calculation_basis_name: watch('calculationBasisName') ?? null,
    calculation_basis_file: watch('calculationBasisFile') ?? null,
    calculation_basis_url: watch('calculationBasisFileUrl') ?? null,
    carbon_offset_flg: watch('carbonOffsetFlg') ?? null,
    other_certification: watch('otherCertification') ?? null,
    reference_unit_price: watch('referenceUnitPrice')
      ? Number(watch('referenceUnitPrice'))
      : null,
    setting_at: dateFormatter(watch('settingAt')) ?? null,
    related_url: watch('relatedUrl') ?? null,
    price_remarks: watch('priceRemarks') ?? null,
    remark: watch('remark') ?? null,
    assignee: watch('assignee') ?? null
  };

  const response = await instance.put(
    `/products/${productId}/${productInfoId}`,
    requestBody,
    {
      headers: {
        'X-XSRF-TOKEN': csrfToken
      }
    }
  );
  return response;
};

// 評価した建材取得
export const getEvaluatedProducts = async (
  limit: number,
  offset: number,
  method: number
): Promise<AxiosResponse> => {
  const response = await instance.get(
    `/evaluations?limit=${limit}&offset=${offset}&method=${method}`,
    {
      headers: {
        'X-XSRF-TOKEN': csrfToken
      }
    }
  );
  return response;
};

// 建材の削除
export const deleteProduct = async (
  productId: number
): Promise<AxiosResponse> => {
  const response = await instance.delete(`/products/${productId}`, {
    headers: {
      'X-XSRF-TOKEN': csrfToken
    }
  });
  return response;
};

// 事務局 建材ステータス変更API
export const changeProductStatus = async (
  productId: number,
  productInfoId: number,
  watch: (fieldName: string) => AdminProductStatusType,
  backDraftFlg?: boolean
) => {
  // 下書きに戻す場合
  if (backDraftFlg) {
    const response = await instance.put(
      `/operator/products/operation/${productId}/${productInfoId}`,
      {
        status: productStatus.draft,
        recommend_flg: Number(watch('recommendFlg'))
      },
      {
        headers: {
          'X-XSRF-TOKEN': csrfToken
        }
      }
    );
    return response;
  }
  const requestBody = {
    status: watch('status'),
    reject_reason: watch('rejectReason') ?? null,
    recommend_flg: Number(watch('recommendFlg')),
    operator_name: watch('operatorName') ?? null,
    operator_memo: watch('operatorMemo') ?? null
  };
  const response = await instance.put(
    `/operator/products/operation/${productId}/${productInfoId}`,
    requestBody,
    {
      headers: {
        'X-XSRF-TOKEN': csrfToken
      }
    }
  );
  return response;
};

// 事務局 建材登録
export const adminRegisterProduct = async (
  kind: number,
  watch: (fieldName: string) => ProductInfosType
): Promise<AxiosResponse> => {
  const settingAt = watch('settingAt');
  const formattedSettingAt = (settingAt as unknown as Dayjs)?.format(
    'YYYY-MM-DD HH:mm:ss'
  );

  const requestBody = {
    kind: kind,
    company_id: Number(watch('company')),
    alias: watch('alias'),
    name: watch('name'),
    specification: watch('specification') ?? null,
    summary: watch('summary'),
    condition: watch('condition') ?? null,
    images: watch('images') ?? null,
    document: watch('document') ?? null,
    document_description_url: watch('documentDescriptionUrl') ?? null,
    category_id: Number(watch('categoryId')) !== 0 ? watch('categoryId') : null,
    material_id: Number(watch('materialId')) !== 0 ? watch('materialId') : null,
    emission_intensity: watch('emissionIntensity')
      ? Number(watch('emissionIntensity'))
      : null,
    reduce_rate: watch('reduceRate') ? Number(watch('reduceRate')) : null,
    calculation_supplement: watch('calculationSupplement') ?? null,
    calculation_basis_type: Number(watch('calculationBasisType')) ?? null,
    calculation_basis_name: watch('calculationBasisName') ?? null,
    calculation_basis_file: watch('calculationBasisFile') ?? null,
    calculation_basis_url: watch('calculationBasisFileUrl') ?? null,
    carbon_offset_flg: watch('carbonOffsetFlg') ?? null,
    other_certification: watch('otherCertification') ?? null,
    reference_unit_price: watch('referenceUnitPrice')
      ? Number(watch('referenceUnitPrice'))
      : null,
    setting_at: formattedSettingAt ?? null,
    price_remarks: watch('priceRemarks') ?? null,
    related_url: watch('relatedUrl') ?? null,
    remark: watch('remark') ?? null,
    assignee: watch('assignee') ?? null,
    recommend_flg: Number(watch('recommendFlg') ?? null),
    operator_name: watch('operatorName') ?? null,
    operator_memo: watch('operatorMemo') ?? null
  };

  const response = await instance.post('/operator/products', requestBody, {
    headers: {
      'X-XSRF-TOKEN': csrfToken
    }
  });

  return response;
};

// 事務局 建材下書き更新API
export const adminSaveDraftProduct = async (
  productId: number,
  productInfoId: number,
  kind: number,
  watch: (fieldName: string) => ProductInfosType
): Promise<AxiosResponse> => {
  const requestBody = {
    kind: kind,
    company_id: Number(watch('company')),
    alias: watch('alias'),
    name: watch('name'),
    specification: watch('specification') ?? null,
    summary: watch('summary'),
    condition: watch('condition') ?? null,
    images: imageMapToArray(watch('images')),
    document: watch('document') ?? null,
    document_description_url: watch('documentDescriptionUrl') ?? null,
    category_id: Number(watch('categoryId')) !== 0 ? watch('categoryId') : null,
    material_id: Number(watch('materialId')) !== 0 ? watch('materialId') : null,
    emission_intensity: watch('emissionIntensity')
      ? Number(watch('emissionIntensity'))
      : null,
    reduce_rate: watch('reduceRate') ? Number(watch('reduceRate')) : null,
    calculation_supplement: watch('calculationSupplement') ?? null,
    calculation_basis_type: Number(watch('calculationBasisType')) ?? null,
    calculation_basis_name: watch('calculationBasisName') ?? null,
    calculation_basis_file: watch('calculationBasisFile') ?? null,
    calculation_basis_url: watch('calculationBasisFileUrl') ?? null,
    carbon_offset_flg: watch('carbonOffsetFlg') ?? null,
    other_certification: watch('otherCertification') ?? null,
    reference_unit_price: watch('referenceUnitPrice')
      ? Number(watch('referenceUnitPrice'))
      : null,
    setting_at: dateFormatter(watch('settingAt')) ?? null,
    related_url: watch('relatedUrl') ?? null,
    remark: watch('remark') ?? null,
    assignee: watch('assignee') ?? null,
    recommend_flg: Number(watch('recommendFlg') ?? null),
    price_remarks: watch('priceRemarks') ?? null,
    operator_name: watch('operatorName') ?? null,
    operator_memo: watch('operatorMemo') ?? null
  };

  const response = await instance.put(
    `/operator/products/${productId}/${productInfoId}`,
    requestBody,
    {
      headers: {
        'X-XSRF-TOKEN': csrfToken
      }
    }
  );
  return response;
};

// 事務局 建材編集
export const adminUpdateProduct = async (
  productId: number,
  watch: (fieldName: string) => ProductInfosType
): Promise<AxiosResponse> => {
  const requestBody = {
    company_id: Number(watch('company')),
    alias: watch('alias'),
    name: watch('name'),
    specification: watch('specification') ?? null,
    summary: watch('summary'),
    condition: watch('condition') ?? null,
    images: imageMapToArray(watch('images')),
    document: watch('document') ? documentData(watch('document')) : null,
    document_description_url: watch('documentDescriptionUrl') ?? null,
    category_id: watch('categoryId'),
    material_id: watch('materialId'),
    emission_intensity: parseToNumberOrNull(watch('emissionIntensity')),
    reduce_rate: watch('reduceRate') ? Number(watch('reduceRate')) : null,
    calculation_supplement: watch('calculationSupplement') ?? null,
    calculation_basis_type: Number(watch('calculationBasisType')),
    calculation_basis_name: watch('calculationBasisName') ?? null,
    calculation_basis_file: watch('calculationBasisFile')
      ? documentData(watch('calculationBasisFile'))
      : null,
    calculation_basis_url: watch('calculationBasisFileUrl') ?? null,
    carbon_offset_flg: watch('carbonOffsetFlg') ?? null,
    other_certification: watch('otherCertification') ?? null,
    reference_unit_price: watch('referenceUnitPrice')
      ? Number(watch('referenceUnitPrice'))
      : null,
    setting_at: dateFormatter(watch('settingAt')) ?? null,
    price_remarks: watch('priceRemarks') ?? null,
    related_url: watch('relatedUrl') ?? null,
    remark: watch('remark') ?? null,
    assignee: watch('assignee') ?? null,
    recommend_flg: Number(watch('recommendFlg') ?? null),
    operator_name: watch('operatorName') ?? null,
    operator_memo: watch('operatorMemo') ?? null
  };

  const response = await instance.post(
    `/operator/products/${productId}`,
    requestBody,
    {
      headers: {
        'X-XSRF-TOKEN': csrfToken
      }
    }
  );

  return response;
};

// 事務局　建材の削除
export const adminDeleteProduct = async (
  productId: number
): Promise<AxiosResponse> => {
  const response = await instance.delete(`/operator/products/${productId}`, {
    headers: {
      'X-XSRF-TOKEN': csrfToken
    }
  });
  return response;
};

// 事務局 建材口コミ取得
export const getAdminProductEvaluation = async (
  productId: number,
  operatorEditFlg?: number
): Promise<AxiosResponse> => {
  const url = operatorEditFlg
    ? `/operator/products/${productId}/evaluations?operator_edit_flg=${operatorEditFlg}`
    : `/operator/products/${productId}/evaluations}`;
  const response = await instance.get(url, {
    headers: {
      'X-XSRF-TOKEN': csrfToken
    }
  });
  return response;
};
